import { Job } from "@ethossoftworks/job"
import { BlocCoordinator } from "@lib/bloc/BlocCoordinator"
import { Router, withRouter } from "@lib/router/router"
import { ExternalRoutes, Routes, smartHubHost } from "@lib/Routes"
import { trackCustomMetric } from "@lib/telemetry"
import { safeParseFloat, safeParseInt } from "@lib/TypeUtil"
import { Asset, AssetDataSource } from "@model/assets/Asset"
import { HourMeterDriftTolerance, OdometerDriftTolerance } from "@model/assets/MeterDriftTolerance"
import { Contact } from "@model/contacts/Contact"
import { WorkOrderRequiredFieldKey } from "@model/company/CompanySettings"
import { serviceItemsWithMissingRequiredFields } from "@model/company/WorkOrderRequiredFieldsUtil"
import { ServiceItemForm } from "@model/serviceRequests/ServiceItemForm"
import { ServiceRequestType, ServiceSubType } from "@model/serviceRequests/ServiceRequest"
import { Site } from "@model/site/Site"
import { PermissionObject, PermissionType, UserData } from "@model/user/User"
import { labelForWorkOrderStatus, WorkOrderStatus, workOrderStatusForValue } from "@model/workOrders/WorkOrder"
import { isWorkOrderDeletable } from "@model/workOrders/WorkOrderDeletionUtil"
import { canEditClosedLockedField } from "@model/workOrders/ClosedWorkOrderLockUtil"
import { WorkOrderPrintOption } from "@model/workOrders/WorkOrderPrintOption"
import { WorkRequestStatus } from "@model/workRequests/WorkRequest"
import { serviceItemFormToWorkRequest } from "@service/workRequests/MainWorkRequestService"
import { CompanyBloc, CompanyState } from "@state/company/CompanyBloc"
import { StatusBloc } from "@state/StatusBloc"
import { UserBloc, UserState } from "@state/user/UserBloc"
import { UserPreferencesBloc } from "@state/UserPreferencesBloc"
import {
    CreateNewWorkOrderScreenBloc,
    CreateNewWorkOrderScreenState,
    newWorkOrderForm,
    WorkOrderEffect,
    WorkOrderForm,
} from "@state/workOrders/CreateNewWorkOrderModalBloc"
import { WorkHistoryOverlayBloc } from "@state/workOrders/WorkHistoryOverlayBloc"
import { ServiceItemsBloc } from "@state/workRequests/ServiceItemsBloc"
import { DI } from "@ui/DI"
import { ServiceItemsViewModel } from "@ui/workRequests/ServiceItemsViewModel"
import isEqual from "lodash.isequal"

type Dependencies = [CreateNewWorkOrderScreenState, CompanyState, UserState]

export type StateSelection = CreateNewWorkOrderScreenState & {
    mechanics: Contact[]
    contacts: Contact[]
    hasDataFetchError: boolean
    assignedToContact: Contact | null
    serviceSubTypes: Record<ServiceRequestType, ServiceSubType>
    workPerformedByContacts: Contact[]
    workPerformedByContactsSelected: Contact[] | null
    openedFormDate: Date
    sites: Site[]
    companyLogoUrl: string | null
}

const CreationWorkOrderStatus = [
    WorkOrderStatus.Pending,
    WorkOrderStatus.Open,
    WorkOrderStatus.InProgress,
    WorkOrderStatus.WorkCompleted,
    WorkOrderStatus.Closed,
]

const IframeCallbackEvents = {
    newWorkOrderCreateSucceededIframeCallback: "newWorkOrderCreateSucceededIframeCallback",
    newWorkOrderCreateErrorIframeCallback: "newWorkOrderCreateErrorIframeCallback",
    newWorkOrderCreateCancelIframeMessage: "newWorkOrderCreateCancelIframeMessage",
}

export class CreateNewWorkOrderScreenViewModel extends BlocCoordinator<Dependencies, StateSelection> {
    private router: Router

    constructor(
        public workOrderBloc: CreateNewWorkOrderScreenBloc,
        public serviceItemsBloc: ServiceItemsBloc,
        private companyBloc: CompanyBloc,
        private statusBloc: StatusBloc,
        private userBloc: UserBloc,
        private userPreferencesBloc: UserPreferencesBloc,
        private workHistoryOverlayBloc: WorkHistoryOverlayBloc
    ) {
        super([workOrderBloc, companyBloc, userBloc])
        this.router = withRouter((router) => router)

        this.serviceItemsBloc.onListItemsChange = (serviceItems: ServiceItemForm[]) => {
            this.UpdateAutomatedStatus()
            // Always recalculate form state when service items change.
            // This ensures formHasChanged is updated to enable/disable the Save button.
            // For Closed Work Orders, UpdateAutomatedStatus returns early without triggering a state update.
            // For non-closed Work Orders, the status might not change, so we still need to recalculate.
            this.workOrderBloc.recalculateFormState()
        }

        // Bug #11217: as items are added to a NEW Work Order, seed the WO Due Date / Odometer / Hour
        // Meter from the closest-to-due Service Item (no-op when editing an existing Work Order).
        this.serviceItemsBloc.onServiceItemAdded = (item: ServiceItemForm) => {
            this.workOrderBloc.applyServiceItemDueValuesForNewWorkOrder(item)
        }
    }

    protected transform = ([workOrderState, companyState, userState]: Dependencies): StateSelection => ({
        ...workOrderState,
        sites: companyState.allSites,
        contacts: companyState.contacts,
        mechanics: companyState.settings.mechanics,
        isLoading: workOrderState.effectStatus[WorkOrderEffect.Fetch].isBusy(),
        hasDataFetchError: workOrderState.effectStatus[WorkOrderEffect.Fetch].isError(),
        assignedToContact: companyState.contacts.find((it) => workOrderState.form.assignedTo === it.id) ?? null,
        serviceSubTypes: companyState.settings.serviceSubTypes,
        workPerformedByContactsSelected:
            workOrderState.form.workPerformedById != null
                ? companyState.contacts.filter((it) => workOrderState.form.workPerformedById!.includes(it.id)) ?? null
                : null,
        workPerformedByContacts: companyState.settings.mechanics,
        openedFormDate: new Date(),
        ReassignWorkOrderModalIsVisible: workOrderState.ReassignWorkOrderModalIsVisible,
        companyLogoUrl: companyState.companyLogoUrl,
    })

    viewWorkOrdersClicked = withRouter((router) => () => router.navigate(Routes.WorkOrders()))

    onMounted = () => {
        //The onMonted is called when the view model is created. But it's not recalled when the modal is opened.
        //For this last behaviour the Init method in the bloc should be used.
    }

    onCloseClicked = (deleteAttachments: boolean = false) => {
        if (!this.state.workOrder?.id) {
            deleteAttachments = true
        }

        this.workOrderBloc.resetForm()
        this.serviceItemsBloc.clearAll(deleteAttachments)

        if (this.state.callerFromMobileApp) this.cancelIframeCallback()
    }

    showModal = () => {
        this.workOrderBloc.Init()
    }

    callerFromMobileAppChanged = (callerFromMobileApp: string) =>
        this.workOrderBloc.callerFromMobileAppChanged(callerFromMobileApp)

    setIsLoading = (value: boolean) => {
        this.workOrderBloc.setIsLoading(value)
    }

    onCancelClicked = () => {
        if (this.state.callerFromMobileApp) this.cancelIframeCallback()

        this.workOrderBloc.hide()
    }

    onCloseAfterSaveClicked = async () => {
        this.workOrderBloc.hideAfterSave()
        this.clearForm()
    }

    clearForm = () => {
        this.workOrderBloc.resetForm()
        this.serviceItemsBloc.clearAll()
        this.workOrderBloc.hide()
        this.workOrderBloc.setHasBeenSaved(false)
    }

    /** Action Item #6766: opens the read-only Work History Overlay for the selected asset. */
    viewWorkHistoryClicked = () => {
        const assetId = this.state.asset?.id
        if (assetId == null) return

        this.workHistoryOverlayBloc.show(assetId)
    }

    loadWorkOrderHistory = (workOrderId: number) => {
        this.workOrderBloc.fetchWorkOrderHistoryData(workOrderId)
    }

    getServiceItems = () => this.serviceItemsBloc.state.items

    /**
     * Missing required fields per Service Item, for the Complete and Closing popups to mark. Empty
     * when the company has selected no Service Item requirements.
     */
    getServiceItemsMissingRequiredFields = () =>
        serviceItemsWithMissingRequiredFields(
            this.getServiceItems(),
            this.companyBloc.state.settings.workOrderRequiredFields.serviceItemFields
        )

    updateServiceItems = (serviceItems: ServiceItemForm[]) => {
        const viewModel = this.serviceItemsViewModel()
        serviceItems.forEach((item) => {
            const itemViewModel = viewModel.getItemViewModel(item.uiElementId)
            itemViewModel!.updateField("status", item.status)
        })
    }

    setCompleteModalOpen = (isOpen: boolean) => {
        this.workOrderBloc.completeModalOpenChanged(isOpen)
    }

    setClosingModalOpen = (isOpen: boolean) => {
        this.workOrderBloc.closingModalOpenChanged(isOpen)
    }

    onStatusDropDownChange = (value: string) => {
        if (value === WorkOrderStatus.WorkCompleted) {
            this.setCompleteModalOpen(true)
        } else if (value === WorkOrderStatus.Closed) {
            // Action Item #10450: skip the Closing Work Order popup when every value it
            // would collect is already present. Users moving a Work Order through the
            // standard flow (Work Completed -> Closed) otherwise see it as a repeat of
            // the Work Completed popup.
            if (this.closingInfoAlreadyComplete()) {
                this.statusChanged(WorkOrderStatus.Closed)
            } else {
                // Action Item #6827: the status is only set to Closed when the user
                // confirms the Closing Work Order popup; cancelling keeps the previous status.
                this.setClosingModalOpen(true)
            }
        } else {
            this.statusChanged(workOrderStatusForValue(value))
            this.setCompleteModalOpen(false)
        }
    }

    // Action Item #10450: true when everything the Closing Work Order popup would
    // collect is already filled in, so the popup can be skipped. Mirrors the popup's
    // confirm rules: a completion date, at least one service item with all of them
    // marked complete, and the physical meters the asset displays.
    closingInfoAlreadyComplete = (): boolean =>
        this.state.form.completionDate != null &&
        this.getServiceItems().length > 0 &&
        this.serviceItemsBloc.areAllServiceItemsInDoneStatus() &&
        this.workOrderBloc.areFormMetersValid()

    UpdateAutomatedStatus = () => {
        //If the Work Order is closed, we don't update automatically the status
        if (this.state.workOrder?.status === WorkOrderStatus.Closed) return

        let status = this.calculateAutomatedStatus(this.state.form)
        this.statusChanged(status)
    }

    calculateAutomatedStatus = (form: WorkOrderForm): WorkOrderStatus => {
        if (this.isInWorkCompletedState()) return WorkOrderStatus.WorkCompleted

        if (this.isInProgressState()) return WorkOrderStatus.InProgress

        if (this.isInOpenState(form)) return WorkOrderStatus.Open

        return WorkOrderStatus.Pending
    }

    isNotEmptyAssignedTo = (form: WorkOrderForm) =>
        form.assignedTo != null && form.assignedTo !== -1 && form.assignedTo !== 0

    isInOpenState = (form: WorkOrderForm) => this.isNotEmptyAssignedTo(form) && !this.inprogressFieldsHasChanged()

    isInProgressState = () => this.inprogressFieldsHasChanged() || this.serviceItemsBloc.serviceItemsHasChanged()

    inprogressFieldsHasChanged = () =>
        !isEqual(this.InprogressFields(this.state.form), this.InprogressFields(newWorkOrderForm(this.state.workOrder)))

    InprogressFields = (form: WorkOrderForm) => {
        //not all the fields from the form triggers a change that will involve in a change to InProgress status
        return {
            urgency: form.urgency,
            //dueDate: form.dueDate,
            site: form.site,
        }
    }

    isInWorkCompletedState = () => {
        return (
            this.serviceItemsBloc.state.items.length > 0 &&
            this.serviceItemsBloc.state.items.every((item) => item.status === WorkRequestStatus.Done)
        )
    }

    hasBeenSaved = (): boolean => {
        return this.state.hasBeenSaved
    }

    serviceItemsViewModel() {
        return new ServiceItemsViewModel(this.serviceItemsBloc, this.statusBloc, this.userBloc, this.workOrderBloc)
    }

    saveClicked = async (serviceItems?: ServiceItemForm[]) => {
        const saveStart = performance.now()
        const isCreate = !this.state.isEditMode

        this.InitiateSavingState()
        const messageId = this.statusBloc.enqueueInfoMessage("Updating work order...")
        const outcome = await this.workOrderBloc.saveForm(serviceItems)

        const saveDuration = performance.now() - saveStart
        const metricName = isCreate ? "work_order_create_ms" : "work_order_save_ms"

        if (!Job.isCancelled(outcome) && outcome.isError()) {
            trackCustomMetric(metricName, saveDuration, { success: "false" })

            let errorMessage = "Error updating work order"
            if (this.state.callerFromMobileApp) {
                this.errorIframeCallback(errorMessage)
                return
            }

            this.statusBloc.enqueueErrorMessage(errorMessage)
        } else {
            trackCustomMetric(metricName, saveDuration, { success: "true" })

            if (this.state.callerFromMobileApp) {
                this.succeededIframeCallback()
                return
            }

            this.statusBloc.hideInfoMessage(messageId)
            this.EndSavingState()
            this.workOrderBloc.setHasBeenSaved(true)

            // Refresh the Work Orders and Service Items lists after successful save
            DI.workOrdersTableBloc().fetchDataWithDelay(500)
            DI.workRequestsTableBloc().fetchDataWithDelay(500)

            this.workOrderBloc.showAfterSave()
            this.workOrderBloc.hide()
        }
    }

    errorIframeCallback = (errorMessage: string) => {
        window?.parent?.postMessage(
            {
                eventName: IframeCallbackEvents.newWorkOrderCreateErrorIframeCallback,
                caller: this.state.callerFromMobileApp,
                data: { errorMessage: errorMessage },
            },
            smartHubHost
        )
    }

    succeededIframeCallback = () => {
        window?.parent?.postMessage(
            {
                eventName: IframeCallbackEvents.newWorkOrderCreateSucceededIframeCallback,
                caller: this.state.callerFromMobileApp,
            },
            smartHubHost
        )
    }

    cancelIframeCallback = () => {
        window?.parent?.postMessage(
            {
                eventName: IframeCallbackEvents.newWorkOrderCreateCancelIframeMessage,
                caller: this.state.callerFromMobileApp,
            },
            smartHubHost
        )
    }

    handleSaveClicked = async () => {
        var canSave = true
        this.InitiateSavingState()

        if (this.shouldShowEmptyWorkOrderModal()) {
            this.workOrderBloc.updateShowEmptyWorkOrderModal(true)
            this.EndSavingState()
            return
        }

        if (this.isClosingWorkOrderCondition()) canSave = await this.handleStateChangedToClose()

        if (canSave) this.saveClicked(this.getServiceItems())
    }

    shouldShowEmptyWorkOrderModal = () =>
        this.getServiceItems().length === 0 &&
        !this.state.emptyWorkOrderModalHasBeenConfirmed &&
        !this.userPreferencesBloc.state.hideEmptyWorkOrderPopup

    emptyWorkOrderDoNotShowAgainChanged = (isChecked: boolean) =>
        this.workOrderBloc.emptyWorkOrderModalDoNotShowAgainChanged(isChecked)

    emptyWorkOrderCancelClicked = () => {
        this.workOrderBloc.updateShowEmptyWorkOrderModal(false)
        this.workOrderBloc.emptyWorkOrderModalConfirmed(false)
        this.workOrderBloc.emptyWorkOrderModalDoNotShowAgainChanged(false)
        this.EndSavingState()
    }

    emptyWorkOrderConfirmClicked = () => {
        if (this.state.emptyWorkOrderModalDoNotShowAgain) {
            this.userPreferencesBloc.updateHideEmptyWorkOrderPopup(true)
        }

        this.workOrderBloc.emptyWorkOrderModalConfirmed(true)
        this.workOrderBloc.updateShowEmptyWorkOrderModal(false)
        this.handleSaveClicked()
    }

    printClicked = () => {
        this.workOrderBloc.showPrintList(true)
    }

    hidePrintList = () => {
        this.workOrderBloc.showPrintList(false)
    }

    viewPartsClicked = () => {
        this.workOrderBloc.showPartsList(true)
        if (this.state.asset?.id) this.workOrderBloc.loadParts(this.state.asset?.id)
    }

    meterSyncInfoClicked = () => {
        this.workOrderBloc.showMeterSyncInfo(true)
    }
    meterSyncInfoCloseButtonClicked = () => {
        this.workOrderBloc.showMeterSyncInfo(false)
    }

    InitiateSavingState = () => this.workOrderBloc.setIsSaving(true)
    EndSavingState = () => this.workOrderBloc.setIsSaving(false)

    getUser = () => this.userBloc.state.user

    getSitesOptions() {
        return [
            ...[{ id: -1, value: "-1", label: "Unknown" }].map((s) => ({
                key: s.id,
                value: `${s.value}`,
                label: s.label,
            })),
            ...this.state.sites.map((it) => ({
                label: it.name,
                value: it.id.toString(),
                key: it.id,
            })),
        ]
    }

    getWorkOrderStatusOptions() {
        let isClosed = this.state.workOrder?.status === WorkOrderStatus.Closed
        let creationWorkOrderStatus =
            !this.hasWorkOrderClosePermission() && !isClosed
                ? CreationWorkOrderStatus.filter((it) => it !== WorkOrderStatus.Closed)
                : CreationWorkOrderStatus

        return creationWorkOrderStatus.map((it) => ({
            label: labelForWorkOrderStatus(it),
            value: it,
            isDisabled: (it === WorkOrderStatus.Closed && this.getServiceItems().length === 0) || isClosed,
            tooltip:
                it === WorkOrderStatus.Closed && this.getServiceItems().length === 0
                    ? "The Work Order must have at least one Service Item to be closed."
                    : undefined,
        }))
    }

    getMechanicById(id: number | null): Contact | null {
        if (!id) return null
        return this.state.mechanics.filter((it) => it.id.toString() === id.toString())[0] ?? null
    }

    getCreatedByDisplayData(createdByUserData: UserData | null): Contact | null {
        if (!createdByUserData || !createdByUserData.id) return null

        if (createdByUserData.personId) {
            // First, try to find the contact in the contacts array
            const foundContact = this.state.contacts.filter(
                (it) => it.id.toString() === createdByUserData.personId!.toString()
            )[0]

            if (foundContact) {
                return foundContact
            }
        }

        // If not found, create a Contact from createdByUserData just for display purposes
        // Build a new Contact object from user data
        const contactFromUser: Contact = {
            id: createdByUserData.personId || createdByUserData.id, // Use personID if available, otherwise use user id
            firstName: createdByUserData.firstName,
            lastName: createdByUserData.lastName,
            displayName: `${createdByUserData.firstName} ${createdByUserData.lastName}`.trim(),
            title: null,
            companyName: null,
            company: {
                id: createdByUserData.companyId,
                name: null,
            },
            address: null,
            city: null,
            state: null,
            postalCode: null,
            officePhone: null,
            homePhone: null,
            cellPhone: null,
            email: null,
            notes: null,
            isMechanic: false,
            personTypeID: null,
            hasActiveWorkOrders: false,
            deleted: false,
        }

        return contactFromUser
    }

    getContactsById(id: number | null): Contact | null {
        if (!id) return null
        return this.state.contacts.filter((it) => it.id.toString() === id.toString())[0] ?? null
    }

    partListViewCloseButtonClicked = () => {
        this.workOrderBloc.showPartsList(false)
    }

    assignedToChanged = (contactId: number) => {
        this.workOrderBloc.assignedToChanged(contactId)
        this.UpdateAutomatedStatus()
    }

    notifyContactsChanged = (contactIds: number[]) => this.workOrderBloc.notifyContactsChanged(contactIds)

    workPerformedByChanged = (contactIds: number[]) => this.workOrderBloc.workPerformedByChanged(contactIds)

    selectedSiteChanged = (value: Site | null) => {
        this.workOrderBloc.siteChanged(value)
        this.UpdateAutomatedStatus()
    }

    selectedAssetChanged = (asset: Asset | null) => {
        this.workOrderBloc.assetChanged(asset)
    }

    loadDefaultSelectedAsset = (assetId: number) => this.workOrderBloc.loadDefaultSelectedAsset(assetId)
    setReassignWorkOrderModalIsVisible = (value: boolean) => {
        this.workOrderBloc.setReassignWorkOrderModalIsVisible(value)
    }
    specialInstructionsChanged = (specialInstructions: string) =>
        this.workOrderBloc.specialInstructionsChanged(specialInstructions)

    urgencyChanged = (urgency: boolean) => {
        this.workOrderBloc.urgencyChanged(urgency)
        this.workOrderBloc.statusChanged(this.calculateAutomatedStatus(this.state.form))
        if (this.state.form.urgency) {
            const messageId = this.statusBloc.enqueueInfoMessage(
                "This work order will be prioritized for immediate attention...."
            )

            setTimeout(() => {
                this.statusBloc.hideInfoMessage(messageId)
            }, 1000)
        }
    }

    completionDateChanged = (completionDate: Date | null) => {
        this.workOrderBloc.completionDateChanged(completionDate)
        this.updateAssetUsagePriorToCompletionDate()
    }

    updateAssetUsagePriorToCompletionDate = () => {
        //this method should be called only when the completion date is set
        //This will get the latest asset usage existing prior to the completion date
        //This is used to show the latest odometer/hourmeter values from Smart Hub
        if (this.state.asset && this.state.form.completionDate) {
            this.workOrderBloc.fetchAssetUsagePriorToCompletionDate(this.state.asset.id, this.state.form.completionDate)
        }
    }

    dueDateChanged = (dueDate: Date) => {
        this.workOrderBloc.dueDateChanged(dueDate)
        this.UpdateAutomatedStatus()
    }

    // Action Item #7751: WO-level Due meter handlers.
    dueHourMeterChanged = (dueHourMeter: string) => {
        this.workOrderBloc.dueHourMeterChanged(dueHourMeter)
    }

    dueOdometerChanged = (dueOdometer: string) => {
        this.workOrderBloc.dueOdometerChanged(dueOdometer)
    }

    physicalHourMeterChanged = (physicalMeter: string) => {
        this.workOrderBloc.physicalHourMeterChanged(physicalMeter)
    }

    onHoldChanged = (onHold: boolean) => {
        this.workOrderBloc.onHoldChanged(onHold)
        this.workOrderBloc.showOnHoldModalChanged(onHold)
        if (!onHold) {
            this.workOrderBloc.onHoldReasonChanged("")
        }
    }

    onHoldReasonChanged = (onHoldReason: string) => this.workOrderBloc.onHoldReasonChanged(onHoldReason)

    showOnHoldModalChanged = (show: boolean) => this.workOrderBloc.showOnHoldModalChanged(show)

    physicalOdometerChanged = (physicalOdometer: string) => {
        this.workOrderBloc.physicalOdometerChanged(physicalOdometer)
    }

    statusChanged = (status: WorkOrderStatus) => {
        this.workOrderBloc.statusChanged(status)

        if (
            status == WorkOrderStatus.Open ||
            (this.state.form.openedDate == null &&
                (status == WorkOrderStatus.InProgress ||
                    status == WorkOrderStatus.WorkCompleted ||
                    status == WorkOrderStatus.Closed))
        ) {
            this.workOrderBloc.openedDateChanged(new Date())
        }
    }

    odometerLifetimeOdometerEditClicked = () => {
        window.open(ExternalRoutes.SmartHubAssetSync(this.state.asset?.id ?? 0), "_blank")
    }

    isServiceDateTimeOtsideExpectedRange = (serviceDateTime: Date): boolean =>
        serviceDateTime > this.state.openedFormDate

    hourMeterLifetimeHourMeterEditClicked = () => {
        window.open(ExternalRoutes.SmartHubAssetSync(this.state.asset?.id ?? 0), "_blank")
    }

    handleReassignWorkOrderClicked = (assignedTo: number, dueDate: Date | null) => {
        this.assignedToChanged(assignedTo)
        // Bug #11217: only apply an explicitly-chosen due date. Assigning a mechanic must not
        // auto-populate an empty Work Order Due Date with today.
        if (dueDate != null) this.dueDateChanged(dueDate)

        if (this.state.form.workPerformedById.filter((x) => x.toString() === assignedTo.toString()).length === 0)
            this.workPerformedByChanged([...this.state.form.workPerformedById, assignedTo])

        //send work order logic
        this.workOrderBloc.setReassignWorkOrderModalIsVisible(false)
    }

    workOrderToPrintChanged = (workOrderId: number | null) => this.workOrderBloc.workOrderToPrintChanged(workOrderId)

    workOrderPrintOptionSelected = async (printOption: WorkOrderPrintOption) =>
        this.workOrderBloc.printWorkOrder(
            printOption,
            this.companyBloc.state.contacts,
            Object.values(this.companyBloc.state.serviceCodes),
            this.companyBloc.state.allSites
        )

    hasWorkOrderEditPermission = () =>
        this.userBloc.state.user.hasAccess(PermissionObject.MaintenanceWorkOrder, PermissionType.Edit)

    hasWorkOrderDeletePermission = () =>
        this.userBloc.state.user.hasAccess(PermissionObject.MaintenanceWorkOrder, PermissionType.Delete)

    hasWorkRequestViewPermission = () =>
        this.userBloc.state.user.hasAccess(PermissionObject.WorkRequest, PermissionType.View)

    hasWorkRequestAddPermission = () =>
        this.userBloc.state.user.hasAccess(PermissionObject.WorkRequest, PermissionType.Add)

    hasWorkRequestDeletePermission = () =>
        this.userBloc.state.user.hasAccess(PermissionObject.WorkRequest, PermissionType.Delete)

    hasRedYellowTagAddPermission = () =>
        this.userBloc.state.user.hasAccess(PermissionObject.RedYellowTag, PermissionType.Add)

    hasWorkOrderClosePermission = () =>
        this.userBloc.state.user.hasAccess(PermissionObject.CloseWorkOrder, PermissionType.Execute)

    hasSiteAddPermission = () => this.userBloc.state.user.hasAccess(PermissionObject.Site, PermissionType.Add)

    /**
     * Action Item #10455: a Closed Work Order can be deleted without first putting the form into
     * edit mode, but doing so requires the Close Work Order - Execute permission on top of the
     * Work Order - Delete permission.
     */
    canDeleteWorkOrder = () =>
        isWorkOrderDeletable(this.state.workOrder?.status, this.state.form?.onHold ?? false, {
            canDelete: this.hasWorkOrderDeletePermission(),
            canClose: this.hasWorkOrderClosePermission(),
        })

    isClosedWorkOrder = () => this.state.workOrder?.status === WorkOrderStatus.Closed

    setEditMode = (workOrderId: number, assetId: number) => {
        this.workOrderBloc.setEditMode(workOrderId, assetId)
    }

    setClosedWorkOrderEditMode = (value: boolean) => {
        this.workOrderBloc.setClosedWorkOrderEditMode(value)
    }

    handleOnSaveButtonClicked = async () => {
        if (this.state.workOrder?.status == WorkOrderStatus.Closed && !this.state.closedWorkOrderEditMode) {
            this.setClosedWorkOrderEditMode(true)
        } else {
            await this.handleSaveClicked()
            this.setClosedWorkOrderEditMode(false)
        }
    }

    saveButtonLabel = () => {
        return this.state.isEditMode
            ? this.state.workOrder?.status == WorkOrderStatus.Closed && !this.state.closedWorkOrderEditMode
                ? "Edit"
                : "Save"
            : "Create Work Order"
    }

    dismissWorkOrderClicked = () => {
        let workRequests = this.getServiceItems().map((item) => serviceItemFormToWorkRequest(item))
        this.workOrderBloc.updateWorkRequest(workRequests)
        this.workOrderBloc.workOrderDismissChanged("", this.state.workOrderDismissal?.workRequestsToDelete ?? [])
    }

    dismissWorkOrderCancelClicked = () => this.workOrderBloc.workOrderDismissCancelled()

    dismissWorkOrderReasonChanged = (reason: string) =>
        this.workOrderBloc.workOrderDismissChanged(reason, this.state.workOrderDismissal?.workRequestsToDelete ?? [])

    dismissWorkOrderWorkRequestsToDismissChanged = (workRequestId: number, isChecked: boolean) => {
        const filtered = (this.state.workOrderDismissal?.workRequestsToDelete ?? []).filter(
            (it) => it !== workRequestId
        )
        var workRequestsToDelete = [...filtered, ...(isChecked ? [workRequestId] : [])]

        this.workOrderBloc.workOrderDismissChanged(this.state.workOrderDismissal?.reason ?? "", workRequestsToDelete)
    }

    dismissWorkOrderConfirmClicked = withRouter((router) => async (reason: string, workRequestsToDelete: number[]) => {
        this.setIsLoading(true)
        this.workOrderBloc.workOrderDismissCancelled()
        const infoMessage = this.statusBloc.enqueueInfoMessage("Deleting work order...")
        const outcome = await this.workOrderBloc.dismissWorkOrder(reason, workRequestsToDelete)

        if (!Job.isCancelled(outcome) && outcome.isError()) {
            this.statusBloc.enqueueErrorMessage("Error deleting work order")
            this.statusBloc.hideInfoMessage(infoMessage)
            return
        }

        this.statusBloc.hideInfoMessage(infoMessage)
        this.setIsLoading(false)
        this.onCancelClicked()
    })

    completionDateOlderThanInstallDateCondition = () =>
        this.state.asset?.InstallDate != null &&
        this.state.form.completionDate != null &&
        this.state.asset.InstallDate < this.state.form.completionDate

    handleStateChangedToClose = async () => {
        let endOfAssetLabel = this.state.asset?.label?.substring(this.state.asset?.label?.length - 5)
        let assetPairedAndNotOEM = endOfAssetLabel?.includes("+") && !endOfAssetLabel?.includes("@")

        const outcome = await this.workOrderBloc.checkHaveBeenSyncBeforeServiceDate()
        if (outcome.isError()) {
            this.statusBloc.enqueueErrorMessage("Error on checkHaveBeenSyncBeforeServiceDate")
            this.EndSavingState()
            return false
        }

        let hasBeenSyncAfterServiceDate = outcome.value.value.length > 0

        if (!hasBeenSyncAfterServiceDate) {
            //if the meters are inbetween the expected range then the meter sync is performed automatically.
            if (
                !this.areMetersOutRange() &&
                this.state.workOrder?.status !== WorkOrderStatus.Closed &&
                this.state.asset?.AssetDataSource !== AssetDataSource.OEMDevice &&
                this.completionDateOlderThanInstallDateCondition()
            )
                this.workOrderBloc.updateMeterSyncRequired(true)

            //This is for scenarios where the meters are out of range then a pop up should be shown to recommend the user to sync the meters before closing the work order.
            if (
                this.areMetersOutRange() &&
                !this.state.syncModalHasBeenShown &&
                assetPairedAndNotOEM &&
                this.completionDateOlderThanInstallDateCondition()
            ) {
                if (this.state.workOrder?.status !== WorkOrderStatus.Closed) {
                    this.workOrderBloc.updateShowMeterSync(true)
                    this.workOrderBloc.syncModalHasBeenShown(true)
                }

                return false
            }
        }

        return true
    }

    areMetersOutRange = (): boolean => {
        return (
            this.isOdometerOutsideExpectedRange(
                this.state.form.physicalOdometer,
                this.state.LatestAssetUsage?.assetReading.odometer,
                this.state.asset?.displayOdometer
            ) ||
            this.isHourMeterOutsideExpectedRange(
                this.state.form.physicalHourMeter,
                this.state.LatestAssetUsage?.assetReading.hourMeter,
                this.state.asset?.displayHourMeter
            )
        )
    }

    isHourMeterOutsideExpectedRange = (
        completedHourMeter: any,
        expectedHourMeter: any,
        displayHourMeter: any
    ): boolean => {
        if (displayHourMeter != null && !displayHourMeter) {
            return false
        }

        return this.isOutsideMeterDriftTolerance(
            safeParseFloat(completedHourMeter),
            safeParseFloat(expectedHourMeter),
            null,
            HourMeterDriftTolerance.max
        )
    }

    isOdometerOutsideExpectedRange = (completedOdometer: any, expectedOdometer: any, displayOdometer: any): boolean => {
        if (displayOdometer != null && !displayOdometer) {
            return false
        }
        return this.isOutsideMeterDriftTolerance(
            safeParseInt(completedOdometer),
            safeParseInt(expectedOdometer),
            null,
            OdometerDriftTolerance.max
        )
    }

    isOutsideMeterDriftTolerance = (
        x: number,
        y: number,
        minDriftTolerance: number | null,
        maxDriftTolerance: number | null
    ): boolean => {
        var minVariance = y + (minDriftTolerance ?? 0)
        var maxVariance = y + (maxDriftTolerance ?? 0)
        var minNegativeVariance = y - (minDriftTolerance ?? 0)
        var maxNegativeVariance = y - (maxDriftTolerance ?? 0)

        var isBelowMin = false
        var isAboveMax = false

        if (minDriftTolerance != null) isBelowMin = x < minVariance || x > minNegativeVariance

        if (maxDriftTolerance != null) isAboveMax = x < maxNegativeVariance || x > maxVariance

        return isBelowMin || isAboveMax
    }

    isServiceDateTimeOutsideExpectedRange = (serviceDateTime: Date): boolean =>
        serviceDateTime > this.state.openedFormDate

    hideMeterSync = () => {
        this.workOrderBloc.updateShowMeterSync(false)
    }

    syncModalHasBeenShown = () => this.workOrderBloc.syncModalHasBeenShown(true)

    meterSyncCancelClicked = () => {
        this.hideMeterSync()
        this.workOrderBloc.setIsSaving(false)
        this.workOrderBloc.updateMeterSyncRequired(false)
        this.workOrderBloc.syncModalHasBeenShown(false)
    }

    meterSyncCloseWithoutSyncClicked = () => {
        this.workOrderBloc.updateMeterSyncRequired(false)
        this.hideMeterSync()
        this.handleSaveClicked()
    }

    meterSyncPerformClicked = () => {
        this.workOrderBloc.updateMeterSyncRequired(true)
        this.hideMeterSync()
        this.handleSaveClicked()
    }

    isSaveButtonEnabled = () => {
        return (
            (this.state.workOrder?.status == WorkOrderStatus.Closed && this.isClosedWorkOrderEditionCondition()) ||
            (this.state.workOrder?.status != WorkOrderStatus.Closed &&
                (this.isNotClosedWorkOrderEditionCondition() || this.isClosingWorkOrderEditionCondition()))
        )
    }

    isClosedWorkOrderEditionCondition = () => {
        return (
            !this.state.closedWorkOrderEditMode ||
            (this.state.closedWorkOrderEditMode &&
                this.state.formHasChanged &&
                this.workOrderBloc.isFormValidCondition() &&
                !this.state.isSaving)
        )
    }

    isNotClosedWorkOrderEditionCondition = () => {
        return (
            this.workOrderBloc.isFormValidCondition() && //The this.state.isFormValid property is not updated in time when running the UpdateAutomatedStatus, so we call the method directly
            (!this.state.isEditMode || (this.state.isEditMode && this.state.formHasChanged)) &&
            !this.isClosingWorkOrderCondition() &&
            this.hasWorkOrderEditPermission() &&
            !this.state.isSaving
        )
    }

    isClosingWorkOrderEditionCondition = () => {
        return (
            this.isClosingWorkOrderCondition() &&
            this.workOrderBloc.isFormValidCondition() && //The this.state.isFormValid property is not updated in time when running the UpdateAutomatedStatus, so we call the method directly
            (!this.state.isEditMode || (this.state.isEditMode && this.state.formHasChanged)) &&
            this.hasWorkOrderClosePermission() &&
            !this.state.isSaving
        )
    }

    isClosingWorkOrderCondition = () =>
        this.state.workOrder?.status != WorkOrderStatus.Closed && this.state.form.status == WorkOrderStatus.Closed

    //Some componets use the isEnabled property to determine if the field is editable
    isEditionEnabled = () => {
        return (
            this.hasWorkOrderEditPermission() &&
            !this.state.form?.onHold &&
            (this.state.workOrder?.status != WorkOrderStatus.Closed || this.state.closedWorkOrderEditMode)
        )
    }

    // Action Item #11164: see canEditClosedLockedField — locked even in closed-WO edit mode.
    isClosedLockedFieldEditable = () =>
        canEditClosedLockedField(
            this.state.workOrder?.status,
            this.hasWorkOrderEditPermission(),
            this.state.form?.onHold ?? false
        )

    displayPhysicalOdometerRequiredLegend = () =>
        this.state.asset &&
        this.state.asset.displayOdometer &&
        (this.state.form?.status === WorkOrderStatus.Closed ||
            this.state?.form.status === WorkOrderStatus.WorkCompleted) &&
        (this.state.form.physicalOdometer == null || this.state.form.physicalOdometer == "") &&
        this.state.workOrder?.status !== WorkOrderStatus.Closed // this is the status from database. Closed work orders shouldn't enforce odometer

    /**
     * Action Item #10448: red outline + legend for the company's required Work Order fields.
     * Derived from the same bloc method the Save button uses, so the highlight can never disagree
     * with whether Save is actually enabled. Only shown while completing or closing, and never on a
     * Work Order that is already Closed in the database, matching the physical meter legends.
     */
    displayAssignedToRequiredLegend = () => this.isWorkOrderRequiredFieldMissing("assignedTo")

    displaySiteRequiredLegend = () => this.isWorkOrderRequiredFieldMissing("site")

    private isWorkOrderRequiredFieldMissing = (field: WorkOrderRequiredFieldKey) =>
        (this.state.form?.status === WorkOrderStatus.Closed ||
            this.state.form?.status === WorkOrderStatus.WorkCompleted) &&
        this.state.workOrder?.status !== WorkOrderStatus.Closed &&
        this.workOrderBloc.missingWorkOrderRequiredFields().includes(field)

    displayPhysicalHourmeterRequiredLegend = () =>
        this.state.asset &&
        this.state.asset.displayHourMeter &&
        (this.state.form?.status === WorkOrderStatus.Closed ||
            this.state?.form.status === WorkOrderStatus.WorkCompleted) &&
        (this.state.form.physicalHourMeter == null || this.state.form.physicalHourMeter == "") &&
        this.state.workOrder?.status !== WorkOrderStatus.Closed // this is the status from database. Closed work orders shouldn't enforce hourMeter
}
